import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Plus, Search, Trash2, Key, ShieldAlert, CheckCircle2, AlertTriangle, Copy, Loader2, Wifi, Webhook as WebhookIcon, X, Check } from "lucide-react";
import { toast } from "sonner";
import { enableAppRpc, type AppInfo } from "@/api";
import { EnableTronEnergyModal } from "@/app/components/tron-energy/EnableTronEnergyModal";
import { useAppsStore, useApps, useAppsLoading } from "@/stores";
import { useWalletSignature } from "@/app/hooks/useWalletSignature";
import {
  classifyWalletError,
  getWalletErrorMessageKey,
} from "@/utils/walletError";
import { useWalletKit } from "@web3jskit/walletkit";
import { formatTimestamp, copyToClipboard as copyText, maskMiddle } from "@/utils";
import {
  useDepositBlockchainsStore,
  selectPrimaryBlockchain,
} from "@/stores/modules/depositBlockchains";
import { hasValidChainLogoUrl } from "../components/NetworkInfoCard";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";
import { DataTableStateRow } from "../components/data-table/DataTableStateRow";
import { Switch } from "@/app/components/ui/switch";
import enableRpcWarningIcon from "@/assets/figma/rpc/enable-rpc-warning.svg";

/** Keys table status / push switches (brand green when on). */
const KEY_ROW_SWITCH_CLASS =
  "data-[state=checked]:bg-emerald-500 dark:data-[state=checked]:bg-[#00FFA3] data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700";

/** Keys table row action link base (Figma: text-only, no icon box). */
const KEY_ROW_ACTION_CLASS =
  "inline-flex items-center gap-1 text-xs font-medium leading-normal whitespace-nowrap transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed";

/** Keys 应用列表每页条数（客户端分页） */
const KEYS_PAGE_SIZE = 10;

/** 单个账号允许创建的最大 App Key 数量（与后端约定） */
const MAX_KEYS = 10;

/** Validate webhook URL: must be parseable, http(s) protocol, non-empty host. */
const isValidWebhookUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.host.length > 0
    );
  } catch {
    return false;
  }
};

export function Keys() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apps = useApps();
  const isLoading = useAppsLoading();
  const {
    refreshApps,
    createNewApp,
    resetAppKey,
    removeAppWithSignature,
    updatePushConfig,
    updateApp,
  } = useAppsStore();
  const { getSignature, getAppRpcSignature } = useWalletSignature();
  const { hydrated } = useWalletKit();
  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState("");
  
  // 切换状态的 loading 状态（记录正在切换的应用 ID）
  const [togglingAppId, setTogglingAppId] = useState<string | null>(null);
  
  // 创建应用相关状态
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAppName, setNewAppName] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // 重置 Key 相关状态
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [appToReset, setAppToReset] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const primaryBlockchain = useDepositBlockchainsStore(selectPrimaryBlockchain);
  const [resetModalChainLogoFailed, setResetModalChainLogoFailed] = useState(false);

  useEffect(() => {
    if (isResetModalOpen) setResetModalChainLogoFailed(false);
  }, [isResetModalOpen, primaryBlockchain?.chainLogoUrl]);

  // 启用 RPC 相关状态
  const [isRpcEnableModalOpen, setIsRpcEnableModalOpen] = useState(false);
  const [appToEnableRpc, setAppToEnableRpc] = useState<string | null>(null);
  const [isEnablingRpc, setIsEnablingRpc] = useState(false);

  // Tron 能量租赁
  const [tronModalApp, setTronModalApp] = useState<{ id: string; name: string } | null>(null);
  const isTronEnergyEnabled = (app: AppInfo) => app.energyRentalService.enabled;

  // 删除应用相关状态
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // WebSocket / Webhook 推送配置（弹窗目标状态，由表格开关意图写入，不可在弹窗内再拨）
  const [isWebSocketModalOpen, setIsWebSocketModalOpen] = useState(false);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [currentAppId, setCurrentAppId] = useState<string | null>(null);
  const [wsTargetEnabled, setWsTargetEnabled] = useState(false);
  const [webhookTargetEnabled, setWebhookTargetEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [pushConfigLoading, setPushConfigLoading] = useState<"websocket" | "webhook" | null>(null);

  // 客户端分页：当前页
  const [keysPage, setKeysPage] = useState(1);

  // 加载应用列表
  useEffect(() => {
    refreshApps();
  }, []);

  // 过滤后的应用列表（前端搜索：仅按备注名匹配，与 placeholder "按名称搜索" 文案保持一致）
  const filteredApps = apps.filter((app) => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return true;
    return app.name.toLowerCase().includes(keyword);
  });

  /** 是否已达到密钥数量上限 */
  const isAtKeyLimit = apps.length >= MAX_KEYS;

  // 搜索关键词变化时重置到第 1 页
  useEffect(() => {
    setKeysPage(1);
  }, [searchKeyword]);

  // 删除 / 刷新后若当前页超出范围，自动 clamp 到最后一页
  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredApps.length / KEYS_PAGE_SIZE));
    if (keysPage > tp) setKeysPage(tp);
  }, [filteredApps.length, keysPage]);

  const keysTotalPages = Math.max(
    1,
    Math.ceil(filteredApps.length / KEYS_PAGE_SIZE),
  );
  const pagedApps = filteredApps.slice(
    (keysPage - 1) * KEYS_PAGE_SIZE,
    keysPage * KEYS_PAGE_SIZE,
  );
  const currentApp = currentAppId
    ? apps.find((app) => app.id === currentAppId) ?? null
    : null;
  const isCurrentAppEnabled = currentApp?.status === 1;

  const webhookUrlTrimmed = webhookUrl.trim();
  const webhookUrlInvalid =
    webhookTargetEnabled &&
    webhookUrlTrimmed.length > 0 &&
    !isValidWebhookUrl(webhookUrlTrimmed);
  const wsSaveDisabled =
    !isCurrentAppEnabled ||
    pushConfigLoading !== null ||
    wsTargetEnabled === Boolean(currentApp?.enable_ws);
  const webhookSaveDisabled =
    !isCurrentAppEnabled ||
    pushConfigLoading !== null ||
    (webhookTargetEnabled &&
      (webhookUrlTrimmed.length === 0 || webhookUrlInvalid)) ||
    (webhookTargetEnabled === Boolean(currentApp?.enable_webhook) &&
      (!webhookTargetEnabled ||
        webhookUrlTrimmed === (currentApp?.webhook_url ?? "").trim()));

  // 创建新应用
  const handleCreateKey = async () => {
    if (isCreating) return;
    if (apps.length >= MAX_KEYS) {
      toast.warning(t("keys.toastMaxKeysReached", { max: MAX_KEYS }));
      return;
    }
    const trimmedName = newAppName.trim();

    if (!trimmedName) {
      toast.error(t("keys.toastRemarkRequired"));
      return;
    }
    if (trimmedName.length < 1 || trimmedName.length > 80) {
      toast.error(t("keys.toastRemarkLength", { max: 80 }));
      return;
    }

    setIsCreating(true);
    toast.loading(t("keys.toastCreating"), { id: "create-app" });

    try {
      const result = await createNewApp(trimmedName);

      if (result) {
        setNewlyCreatedKey(result.key);
        setNewAppName("");
        toast.success(t("keys.toastCreated"), { id: "create-app" });
      } else {
        toast.error(t("keys.toastCreateFailed"), { id: "create-app" });
      }
    } catch (error) {
      console.error('[Keys] Create app error:', error);
      toast.error(t("keys.toastCreateFailed"), { id: "create-app" });
    } finally {
      setIsCreating(false);
    }
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewlyCreatedKey(null);
    setResetResult(null);
    setNewAppName("");
  };

  // 切换应用状态（启用 / 禁用）
  // 后端 PATCH /apps/{id}/status 现在强制要求钱包签名，
  // 这里先通过 /apps/challenge + personal_sign 拿到签名，再调用 store。
  const toggleKeyStatus = async (id: string, currentStatus: number) => {
    if (togglingAppId) return;

    setTogglingAppId(id);
    try {
      const sig = await getSignature();
      if (!sig) {
        toast.error(t("common.operationFailed"));
        return;
      }

      const success = await useAppsStore
        .getState()
        .toggleAppStatus(id, currentStatus, sig.signature);

      if (success) {
        const actionText = currentStatus === 1
          ? t("keys.toastAppDisabled")
          : t("keys.toastAppEnabled");
        toast.success(actionText);
      } else {
        toast.error(t("common.operationFailed"));
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"));
        return;
      }
      console.error('[Keys] Failed to toggle app status:', error);
      toast.error(
        code === "unknown"
          ? t("common.operationFailed")
          : t(getWalletErrorMessageKey(code))
      );
    } finally {
      setTogglingAppId(null);
    }
  };

  // 删除应用
  const deleteKey = (id: string) => {
    setAppToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const openWebSocketModal = (appId: string) => {
    const app = apps.find((item) => item.id === appId);
    if (!app || app.status !== 1) return;
    setCurrentAppId(appId);
    setWsTargetEnabled(!app.enable_ws);
    setIsWebSocketModalOpen(true);
  };

  const closeWebSocketModal = () => {
    if (pushConfigLoading) return;
    setIsWebSocketModalOpen(false);
    setCurrentAppId(null);
    setWsTargetEnabled(false);
  };

  const openWebhookModal = (appId: string) => {
    const app = apps.find((item) => item.id === appId);
    if (!app || app.status !== 1) return;
    setCurrentAppId(appId);
    setWebhookTargetEnabled(!app.enable_webhook);
    setWebhookUrl(app.webhook_url ?? "");
    setIsWebhookModalOpen(true);
  };

  const closeWebhookModal = () => {
    if (pushConfigLoading) return;
    setIsWebhookModalOpen(false);
    setCurrentAppId(null);
    setWebhookTargetEnabled(false);
    setWebhookUrl("");
  };

  // Only gate on WalletKit hydration. Missing WalletKit.walletAddress after
  // refresh is common while auth still shows connected — signature uses
  // resolveLiveEip1193Provider (kit / window.ethereum), never Connect Wallet UI.
  const ensureWalletReady = () => {
    if (!hydrated) {
      toast.error(t("keys.toastWalletInitializing"));
      return false;
    }
    return true;
  };

  const handleSaveWebSocket = async () => {
    if (!currentApp || pushConfigLoading) return;
    if (!isCurrentAppEnabled) {
      toast.error(t("keys.websocket.toastAppDisabled"));
      return;
    }
    if (wsTargetEnabled === Boolean(currentApp.enable_ws)) {
      closeWebSocketModal();
      return;
    }
    if (!ensureWalletReady()) return;

    setPushConfigLoading("websocket");
    const toastId = "websocket-config";
    toast.loading(t("keys.websocket.toastSigning"), { id: toastId });
    try {
      const sig = await getSignature();
      if (!sig) {
        toast.error(t("common.operationFailed"), { id: toastId });
        return;
      }
      const success = await updatePushConfig(currentApp.id, {
        enable_ws: wsTargetEnabled,
        signature: sig.signature,
      });
      if (success) {
        toast.success(t("keys.websocket.toastSaved"), { id: toastId });
        setIsWebSocketModalOpen(false);
        setCurrentAppId(null);
        setWsTargetEnabled(false);
      } else {
        toast.error(t("keys.websocket.toastSaveFailed"), { id: toastId });
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"), { id: toastId });
        return;
      }
      console.error("[Keys] Save WebSocket settings error:", error);
      toast.error(
        code === "unknown"
          ? t("keys.websocket.toastSaveFailed")
          : t(getWalletErrorMessageKey(code)),
        { id: toastId }
      );
    } finally {
      setPushConfigLoading(null);
    }
  };

  const handleSaveWebhook = async () => {
    if (!currentApp || pushConfigLoading) return;
    if (!isCurrentAppEnabled) {
      toast.error(t("keys.webhook.toastAppDisabled"));
      return;
    }
    const trimmedUrl = webhookUrl.trim();
    if (webhookTargetEnabled) {
      if (!trimmedUrl) {
        toast.error(t("keys.webhook.toastUrlRequired"));
        return;
      }
      if (!isValidWebhookUrl(trimmedUrl)) {
        toast.error(t("keys.webhook.toastUrlInvalid"));
        return;
      }
    }
    if (!ensureWalletReady()) return;

    setPushConfigLoading("webhook");
    const toastId = "webhook-config";
    toast.loading(t("keys.webhook.toastSigning"), { id: toastId });
    try {
      const sig = await getSignature();
      if (!sig) {
        toast.error(t("common.operationFailed"), { id: toastId });
        return;
      }
      const success = await updatePushConfig(currentApp.id, {
        enable_webhook: webhookTargetEnabled,
        // Enable: persist URL. Disable: omit so existing URL is kept for next enable.
        ...(webhookTargetEnabled
          ? { webhook_url: trimmedUrl }
          : {}),
        signature: sig.signature,
      });
      if (success) {
        toast.success(t("keys.webhook.toastSaved"), { id: toastId });
        setIsWebhookModalOpen(false);
        setCurrentAppId(null);
        setWebhookTargetEnabled(false);
        setWebhookUrl("");
      } else {
        toast.error(t("keys.webhook.toastSaveFailed"), { id: toastId });
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"), { id: toastId });
        return;
      }
      console.error("[Keys] Save webhook error:", error);
      toast.error(
        code === "unknown"
          ? t("keys.webhook.toastSaveFailed")
          : t(getWalletErrorMessageKey(code)),
        { id: toastId }
      );
    } finally {
      setPushConfigLoading(null);
    }
  };

  const confirmDeleteKey = async () => {
    if (!appToDelete || isDeleting) return;

    if (!ensureWalletReady()) return;

    setIsDeleting(true);
    toast.loading(t("keys.toastDeleting"), { id: "delete-app" });

    try {
      const signature = await getSignature();
      if (!signature) {
        toast.error(t("keys.toastDeleteFailed"), { id: "delete-app" });
        return;
      }

      const success = await removeAppWithSignature(appToDelete, signature.signature);
      if (success) {
        toast.success(t("keys.toastDeleted"), { id: "delete-app" });
        setIsDeleteModalOpen(false);
        setAppToDelete(null);
      } else {
        toast.error(t("keys.toastDeleteFailed"), { id: "delete-app" });
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"), { id: "delete-app" });
        return;
      }
      console.error('[Keys] Delete app error:', error);
      toast.error(
        code === "unknown"
          ? t("keys.toastDeleteFailed")
          : t(getWalletErrorMessageKey(code)),
        { id: "delete-app" }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // 重置 API Key
  const handleResetKey = async () => {
    if (!appToReset || isResetting) return;

    if (!ensureWalletReady()) return;

    setIsResetting(true);
    toast.loading(t("keys.toastResetLoading"), { id: "reset-key" });

    try {
      const signature = await getSignature();
      if (!signature) {
        toast.error(t("keys.toastResetFailed"), { id: "reset-key" });
        return;
      }

      const result = await resetAppKey(appToReset, signature.signature);
      if (result) {
        setResetResult(result.key);
        toast.success(t("keys.toastResetOk"), { id: "reset-key" });
        setIsResetModalOpen(false);
        setIsCreateModalOpen(true);
      } else {
        toast.error(t("keys.toastResetFailed"), { id: "reset-key" });
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"), { id: "reset-key" });
        return;
      }
      console.error('[Keys] Reset key error:', error);
      toast.error(
        code === "unknown"
          ? t("keys.toastResetFailed")
          : t(getWalletErrorMessageKey(code)),
        { id: "reset-key" }
      );
    } finally {
      setIsResetting(false);
    }
  };

  const handleRpcClick = (appId: string, rpcEnabled: boolean) => {
    if (rpcEnabled) {
      navigate(`/keys/${appId}/rpc`);
      return;
    }
    setAppToEnableRpc(appId);
    setIsRpcEnableModalOpen(true);
  };

  const handleTronAction = (app: AppInfo) => {
    if (app.energyRentalService.status === "unavailable") return;
    if (isTronEnergyEnabled(app)) {
      navigate(`/keys/${app.id}/tron-energy`);
      return;
    }
    setTronModalApp({ id: app.id, name: app.name });
  };

  const closeRpcEnableModal = () => {
    if (isEnablingRpc) return;
    setIsRpcEnableModalOpen(false);
    setAppToEnableRpc(null);
  };

  const handleEnableRpc = async () => {
    if (!appToEnableRpc || isEnablingRpc) return;

    if (!ensureWalletReady()) return;

    setIsEnablingRpc(true);
    toast.loading(t("keys.toastResetLoading"), { id: "enable-rpc" });

    try {
      const signature = await getAppRpcSignature(appToEnableRpc, "enable");
      if (!signature) {
        toast.error(t("common.operationFailed"), { id: "enable-rpc" });
        return;
      }

      const result = await enableAppRpc(appToEnableRpc, {
        signature: signature.signature,
      });
      if (!result.ok) {
        toast.error(t("common.operationFailed"), { id: "enable-rpc" });
        return;
      }

      const targetAppId = appToEnableRpc;
      const existingRpc = apps.find((a) => a.id === targetAppId)?.rpcService;
      const networkCount =
        result.data.networks?.length ?? existingRpc?.networkCount ?? 0;
      // Optimistic local update so returning to Keys shows enabled without a
      // full-table refreshApps (which would flash isLoading on the list).
      updateApp(targetAppId, {
        rpcService: {
          status: "enabled",
          enabled: true,
          networkCount,
        },
      });

      toast.success(t("keys.toastRpcEnabled"), { id: "enable-rpc" });
      // Keep the modal + button loading until route change; do not close the
      // modal or await refreshApps (that exposed the Keys table reload flicker).
      navigate(`/keys/${targetAppId}/rpc`);
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("keys.toastSignRejected"), { id: "enable-rpc" });
        return;
      }
      console.error("[Keys] Enable RPC error:", error);
      toast.error(
        code === "unknown"
          ? t("common.operationFailed")
          : t(getWalletErrorMessageKey(code)),
        { id: "enable-rpc" },
      );
    } finally {
      setIsEnablingRpc(false);
    }
  };

  const lastUsedLabel = (lastUsedAt: number | null) =>
    formatTimestamp(lastUsedAt, 'date');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t("keys.title")}</h1>
          <p className="text-slate-500 dark:text-slate-400 text-[14px] mx-[0px] mt-[4px] mb-[0px]">{t("keys.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            // 不直接禁用按钮（保持视觉无障碍 / 让用户能感知到反馈），
            // 改为点击时校验：超过上限时友好提示，未超过才打开创建弹窗。
            if (isAtKeyLimit) {
              toast.warning(t("keys.toastMaxKeysReached", { max: MAX_KEYS }));
              return;
            }
            setIsCreateModalOpen(true);
          }}
          className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-5 py-2.5 rounded-full font-bold hover:opacity-90 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t("keys.createNew")}
        </button>
      </div>

      <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-4 bg-slate-50/50 dark:bg-[#0E0F11]/50">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-400" />
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder={t("keys.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#1a1d1f] border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3]/50 outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full table-fixed divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-[#0E0F11]/50">
              <tr>
                <th scope="col" className="pl-6 pr-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-[220px]">
                  {t("keys.colName")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[160px]">
                  {t("keys.colAppId")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[110px]">
                  {t("keys.colKey")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[88px]">
                  {t("keys.colStatus")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[108px]">
                  {t("keys.colWebsocketStatus")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[100px]">
                  {t("keys.colWebhookStatus")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[100px]">
                  {t("keys.colCreated")}
                </th>
                <th scope="col" className="px-2 py-4 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[200px]">
                  {t("keys.colServices")}
                </th>
                <th scope="col" className="pl-2 pr-6 py-4 text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap w-[160px]">
                  {t("keys.colActions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-50 dark:bg-[#1a1d1f] divide-y divide-slate-200 dark:divide-slate-800">
              <DataTableStateRow
                colSpan={9}
                loading={isLoading}
                isEmpty={!isLoading && filteredApps.length === 0}
                emptyIcon={<Key className="h-8 w-8" />}
                emptyTitle={searchKeyword ? t("keys.noSearchResults") : t("keys.emptyTitle")}
                emptyHint={searchKeyword ? t("keys.tryDifferentSearch") : t("keys.emptyHint")}
              />
              {!isLoading && filteredApps.length > 0 && (
                pagedApps.map((app) => (
                  <tr key={app.id} className="hover:bg-white dark:hover:bg-[#0E0F11]/50 transition-colors group">
                    <td className="pl-6 pr-2 py-4 w-[220px]">
                      <div className="flex items-center min-w-0">
                        <div className="min-w-0">
                          <div
                            className="text-sm font-bold text-slate-900 dark:text-slate-200 truncate"
                            title={app.name}
                          >
                            {app.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium truncate">
                            {t("common.lastUsed", { val: lastUsedLabel(app.lastUsedAt) })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-[160px]">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-slate-600 dark:text-slate-400 font-mono tracking-tight text-[12px]" title={app.id}>
                          {maskMiddle(app.id)}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyText(app.id).then(() => toast.success(t("keys.toastAppIdCopied")))}
                          className="p-2 shrink-0 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-[#00FFA3] dark:hover:bg-[#00FFA3]/5 rounded-lg transition-colors border border-transparent dark:hover:border-[#00FFA3]/20"
                          title={t("keys.copyAppId")}
                        >
                          <Copy className="w-[18px] h-[18px]" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-slate-600 dark:text-slate-400 font-mono tracking-tight text-[12px] w-[110px]">
                      {app.keyPrefix}
                      <span className="opacity-50">***</span>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-[88px]">
                      <div className="inline-flex items-center gap-1.5">
                        <Switch
                          checked={app.status === 1}
                          disabled={togglingAppId === app.id}
                          onCheckedChange={() => toggleKeyStatus(app.id, app.status)}
                          aria-label={
                            app.status === 1
                              ? t("keys.titleDisableKey")
                              : t("keys.titleEnableKey")
                          }
                          className={KEY_ROW_SWITCH_CLASS}
                        />
                        {/* Reserve spinner slot so loading does not reflow the column */}
                        <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                          {togglingAppId === app.id && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-[108px]">
                      <Switch
                        checked={Boolean(app.enable_ws)}
                        disabled={
                          app.status !== 1 || pushConfigLoading !== null
                        }
                        onCheckedChange={() => openWebSocketModal(app.id)}
                        aria-label={t("keys.websocket.toggleLabel")}
                        className={KEY_ROW_SWITCH_CLASS}
                      />
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-[100px]">
                      <Switch
                        checked={Boolean(app.enable_webhook)}
                        disabled={
                          app.status !== 1 || pushConfigLoading !== null
                        }
                        onCheckedChange={() => openWebhookModal(app.id)}
                        aria-label={t("keys.webhook.toggleLabel")}
                        className={KEY_ROW_SWITCH_CLASS}
                      />
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium text-[12px] w-[100px]">
                      {formatTimestamp(app.createdAt, 'date')}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-xs font-medium w-[200px]">
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          onClick={() =>
                            handleRpcClick(
                              app.id,
                              Boolean(app.rpcService?.enabled),
                            )
                          }
                          className={`${KEY_ROW_ACTION_CLASS} ${
                            app.rpcService?.enabled
                              ? "text-emerald-600 dark:text-[#00FFA3]"
                              : "text-cyan-600 dark:text-[#22d3ee]"
                          }`}
                          title={t("keys.titleRpc")}
                        >
                          {t("keys.actionRpc")}
                          {app.rpcService?.enabled && (
                            <Check
                              className="w-3.5 h-3.5 shrink-0"
                              strokeWidth={2.5}
                            />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTronAction(app)}
                          disabled={
                            app.energyRentalService.status === "unavailable"
                          }
                          className={`${KEY_ROW_ACTION_CLASS} ${
                            isTronEnergyEnabled(app)
                              ? "text-purple-600 dark:text-purple-400"
                              : "text-slate-500 dark:text-[#90a1b9]"
                          } ${
                            app.energyRentalService.status === "unavailable"
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }`}
                          title={t("keys.titleTronEnergy", "Tron 能量租赁")}
                        >
                          {t("keys.actionTronEnergy", "Tron 能量")}
                          {isTronEnergyEnabled(app) && (
                            <Check
                              className="w-3.5 h-3.5 shrink-0"
                              strokeWidth={2.5}
                            />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="pl-2 pr-6 py-4 whitespace-nowrap text-right text-xs font-medium w-[160px]">
                      <div className="flex items-center justify-end gap-2.5">
                        <Link
                          to={`/keys/${app.id}`}
                          className={`${KEY_ROW_ACTION_CLASS} text-slate-500 dark:text-[#90a1b9]`}
                          title={t("keys.titleViewDetails")}
                        >
                          {t("keys.actionDetails")}
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setAppToReset(app.id);
                            setIsResetModalOpen(true);
                          }}
                          className={`${KEY_ROW_ACTION_CLASS} text-orange-500 dark:text-[#f17313]`}
                          title={t("keys.titleResetKey")}
                        >
                          {t("keys.actionReset")}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteKey(app.id)}
                          className={`${KEY_ROW_ACTION_CLASS} text-red-500 dark:text-[#e64341]`}
                          title={t("keys.titleDeleteKey")}
                        >
                          {t("keys.actionDelete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 pb-4 pt-0">
          <TablePaginationFooter
            page={keysPage}
            totalPages={keysTotalPages}
            totalItems={filteredApps.length}
            onPageChange={setKeysPage}
          />
        </div>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {(newlyCreatedKey || resetResult) ? (
              <div className="p-8 text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 border border-emerald-200 dark:bg-[#00FFA3]/10 dark:border-[#00FFA3]/20 mb-6">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-[#00FFA3]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{t("keys.modalGeneratedTitle")}</h3>
                <div className="bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20 rounded-xl p-4 mb-6 text-left">
                  <div className="flex gap-3">
                    <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800 dark:text-amber-200/90 leading-relaxed">
                      <strong>{t("common.important")}</strong> {t("keys.modalImportantBody")}
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4 mb-8">
                  <code className="text-sm font-mono font-bold text-slate-800 dark:text-slate-200 break-all select-all text-left">{newlyCreatedKey || resetResult}</code>
                  <button
                    type="button"
                    onClick={() => (newlyCreatedKey || resetResult) && copyText(newlyCreatedKey || resetResult || '').then(() => toast.success(t("keys.toastKeyCopied")))}
                    className="p-2 shrink-0 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-[#00FFA3] dark:hover:bg-[#00FFA3]/5 rounded-lg transition-colors border border-transparent dark:hover:border-[#00FFA3]/20"
                    title={t("keys.copyKeyTitle")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="w-full bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full px-4 py-3 font-bold hover:opacity-90 transition-colors"
                >
                  {t("keys.modalCopiedDone")}
                </button>
              </div>
            ) : (
              <div className="p-8">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">{t("keys.modalCreateTitle")}</h3>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                      {t("keys.remarkLabel")}
                      <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">
                        {t("keys.remarkLengthHint", { max: 80 })}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={newAppName}
                      onChange={(e) => setNewAppName(e.target.value)}
                      placeholder={t("keys.remarkPlaceholder")}
                      maxLength={80}
                      className="w-full px-4 py-3 bg-white dark:bg-[#0E0F11] border border-slate-300 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3] outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                      {t("keys.remarkLengthCounter", { current: newAppName.length, max: 80 })}
                    </p>
                  </div>
                  <div className="flex gap-3 mt-8">
                    <button
                      type="button"
                      onClick={closeCreateModal}
                      disabled={isCreating}
                      className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#D2F0E1]/5 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-[#141517] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateKey}
                      disabled={isCreating || !newAppName.trim() || newAppName.trim().length > 80}
                      className="flex-1 px-4 py-3 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t("keys.generateKey")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isRpcEnableModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-[486px] flex flex-col gap-8 items-center overflow-hidden rounded-3xl border border-slate-200 dark:border-[#1d293d] bg-white dark:bg-[rgba(210,240,225,0.05)] p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-full flex flex-col gap-6 items-center">
              <div className="w-full flex flex-col gap-4 items-center">
                <div className="relative shrink-0 size-[80px]">
                  <img
                    src={enableRpcWarningIcon}
                    alt=""
                    width={80}
                    height={80}
                    className="block size-full"
                  />
                </div>
                <div className="w-full flex flex-col gap-2.5 items-center justify-center px-12 text-center">
                  <h3 className="w-full text-xl font-bold leading-[1.5] text-slate-900 dark:text-white">
                    {t("keys.rpcEnableTitle")}
                  </h3>
                  <p className="w-full text-sm font-normal leading-[1.5] text-slate-500 dark:text-[#90a1b9]">
                    {t("keys.rpcEnableHint")}
                  </p>
                </div>
              </div>
              <div className="w-full flex items-center justify-center rounded-xl border border-slate-200 dark:border-[#1d293d] bg-slate-50 dark:bg-[#0e0f11] p-3">
                <p className="text-base font-normal leading-[1.5] text-center text-slate-500 dark:text-[#90a1b9]">
                  {t("keys.rpcEnableHint")}
                </p>
              </div>
            </div>
            <div className="w-full flex gap-4 items-center">
              <button
                type="button"
                onClick={closeRpcEnableModal}
                disabled={isEnablingRpc}
                className="flex-1 min-w-0 flex items-center justify-center rounded-full border border-slate-300 dark:border-[#1d293d] bg-slate-100 dark:bg-[rgba(210,240,225,0.05)] p-4 text-base font-normal text-slate-900 dark:text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleEnableRpc}
                disabled={isEnablingRpc}
                className="flex-1 min-w-0 flex items-center justify-center gap-2 rounded-full border border-slate-300 dark:border-[#1d293d] bg-emerald-500 dark:bg-[#00FFA3] p-4 text-base font-normal text-white dark:text-[#030e12] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEnablingRpc && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("keys.rpcEnableConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 max-w-md w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20 mb-6">
              <AlertTriangle className="h-8 w-8 text-orange-600 dark:text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white text-center mb-3">{t("keys.resetTitle")}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8 leading-relaxed">{t("keys.resetBody")}</p>

            <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-slate-800 mb-8 flex items-center gap-3">
              <div className="bg-slate-50 dark:bg-[#1a1d1f] p-1.5 rounded-md border border-slate-200 dark:border-slate-800">
                {primaryBlockchain != null &&
                hasValidChainLogoUrl(primaryBlockchain.chainLogoUrl) &&
                !resetModalChainLogoFailed ? (
                  <img
                    src={primaryBlockchain.chainLogoUrl.trim()}
                    alt={primaryBlockchain.chainName}
                    className="w-5 h-5 rounded-full object-cover"
                    onError={() => setResetModalChainLogoFailed(true)}
                  />
                ) : (
                  <div
                    className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse"
                    aria-hidden
                  />
                )}
              </div>
              <span className="text-sm font-bold text-slate-900 dark:text-slate-200">
                {t("keys.walletSignature")}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsResetModalOpen(false);
                  setAppToReset(null);
                }}
                disabled={isResetting}
                className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#D2F0E1]/5 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-[#141517] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleResetKey}
                disabled={isResetting}
                className="flex-1 px-4 py-3 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isResetting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("keys.signReset")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 max-w-md w-full p-8 animate-in fade-in zoom-in-95 duration-200">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 mb-6">
              <Trash2 className="h-8 w-8 text-red-600 dark:text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white text-center mb-3">{t("keys.deleteTitle")}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8 leading-relaxed">{t("keys.deleteBody")}</p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setAppToDelete(null);
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#D2F0E1]/5 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-[#141517] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={confirmDeleteKey}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isWebSocketModalOpen && currentApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar animate-in fade-in zoom-in-95 duration-200">
            <div className="sticky top-0 bg-slate-50 dark:bg-[#1a1d1f] border-b border-slate-200 dark:border-slate-800 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-emerald-50 dark:bg-[#00FFA3]/10 rounded-lg">
                  <Wifi className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {wsTargetEnabled
                      ? t("keys.websocket.enableTitle")
                      : t("keys.websocket.disableTitle")}
                  </h3>
                  <p className="mt-1 text-xs font-mono text-slate-500 dark:text-slate-400 truncate">
                    {currentApp.id}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeWebSocketModal}
                disabled={pushConfigLoading !== null}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t("common.cancel")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {wsTargetEnabled
                    ? t("keys.websocket.enableDescription")
                    : t("keys.websocket.disableDescription")}
                </p>
              </div>

              {!isCurrentAppEnabled && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("keys.websocket.disabledHint")}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeWebSocketModal}
                  disabled={pushConfigLoading !== null}
                  className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#D2F0E1]/5 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-[#141517] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveWebSocket}
                  disabled={wsSaveDisabled}
                  className="flex-1 px-4 py-3 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {pushConfigLoading === "websocket" && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {wsTargetEnabled
                    ? t("keys.websocket.save")
                    : t("keys.websocket.confirmDisable")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isWebhookModalOpen && currentApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar animate-in fade-in zoom-in-95 duration-200">
            <div className="sticky top-0 bg-slate-50 dark:bg-[#1a1d1f] border-b border-slate-200 dark:border-slate-800 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-emerald-50 dark:bg-[#00FFA3]/10 rounded-lg">
                  <WebhookIcon className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {webhookTargetEnabled
                      ? t("keys.webhook.enableTitle")
                      : t("keys.webhook.disableTitle")}
                  </h3>
                  <p className="mt-1 text-xs font-mono text-slate-500 dark:text-slate-400 truncate">
                    {currentApp.id}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeWebhookModal}
                disabled={pushConfigLoading !== null}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t("common.cancel")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-white dark:bg-[#0E0F11] rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {webhookTargetEnabled
                    ? t("keys.webhook.enableDescription")
                    : t("keys.webhook.disableDescription")}
                </p>
              </div>

              {webhookTargetEnabled ? (
                <>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                      {t("keys.webhook.urlLabel")}
                    </label>
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      disabled={!isCurrentAppEnabled || pushConfigLoading !== null}
                      placeholder={t("keys.webhook.placeholder")}
                      className={`w-full px-4 py-3 bg-white dark:bg-[#0E0F11] border rounded-xl focus:ring-2 outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-70 disabled:cursor-not-allowed ${
                        webhookUrlInvalid
                          ? "border-red-400 dark:border-red-500/60 focus:ring-red-300 dark:focus:ring-red-500/40 focus:border-red-500"
                          : "border-slate-300 dark:border-slate-800 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3]"
                      }`}
                    />
                    {webhookUrlInvalid && (
                      <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                        {t("keys.webhook.toastUrlInvalid")}
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl p-4 border border-blue-200 dark:border-blue-500/20">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-blue-900 dark:text-blue-200 mb-1">
                          {t("keys.webhook.signatureTitle")}
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                          {t("keys.webhook.signatureBody")}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                currentApp.webhook_url && (
                  <div>
                    <p className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                      {t("keys.webhook.urlLabel")}
                    </p>
                    <p className="w-full px-4 py-3 bg-slate-100 dark:bg-[#0E0F11] border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-mono text-slate-600 dark:text-slate-400 break-all">
                      {currentApp.webhook_url}
                    </p>
                  </div>
                )
              )}

              {!isCurrentAppEnabled && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("keys.webhook.disabledHint")}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeWebhookModal}
                  disabled={pushConfigLoading !== null}
                  className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#D2F0E1]/5 rounded-full font-bold hover:bg-slate-50 dark:hover:bg-[#141517] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveWebhook}
                  disabled={webhookSaveDisabled}
                  className="flex-1 px-4 py-3 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {pushConfigLoading === "webhook" && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {webhookTargetEnabled
                    ? t("keys.webhook.save")
                    : t("keys.webhook.confirmDisable")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <EnableTronEnergyModal
        isOpen={Boolean(tronModalApp)}
        appId={tronModalApp?.id || null}
        appName={tronModalApp?.name}
        onClose={() => setTronModalApp(null)}
        onSuccess={(appId) => {
          setTronModalApp(null);
          refreshApps();
          navigate(`/keys/${appId}/tron-energy`);
        }}
      />
    </div>
  );
}
